import type { Persona } from "@/lib/api";

/** One persona that already has reports on a job, with its cached languages and state. */
export interface PersonaVariant {
  persona: Persona;
  languages: string[];
  role_name: string;
  vertical: string;
  /** Canonical role for this persona — sent back on a switch so role, scenario and persona
   *  move together. `null` for personas no role maps to (auto, transcript). */
  role_slug: string | null;
  /** Reports still CREATED/PROCESSING for this persona (TRANSCRIPT/SENTIMENT excluded). */
  pending: number;
  /** Reports that ended FAILED for this persona. */
  failed: number;
}

/** A role the user may add to this job; `generated` marks the ones already present. */
export interface AddablePersona {
  role_slug: string;
  role_name: string;
  vertical: string;
  persona: Persona;
  generated: boolean;
}

export interface AvailableVariants {
  variants: PersonaVariant[];
  addable: AddablePersona[];
}

export type PersonaState = "ready" | "generating" | "failed";

/** Generation still running wins over failures — the user can only act once it settles. */
export function personaState(variant: Pick<PersonaVariant, "pending" | "failed">): PersonaState {
  if (variant.pending > 0) return "generating";
  if (variant.failed > 0) return "failed";
  return "ready";
}

const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  ru: "Russian",
  es: "Spanish",
  de: "German",
  fr: "French",
  pt: "Portuguese",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  ar: "Arabic",
};

/** Display code for a stored language value ("" = source language). */
export function langCode(language: string, sourceLanguage?: string): string {
  if (language === "") return (sourceLanguage || "src").toUpperCase();
  return language.toUpperCase();
}

/** Friendly language name for a dropdown row; "" resolves to the source language. */
export function langName(language: string, sourceLanguage?: string): string {
  const code = (language || sourceLanguage || "").toLowerCase();
  const name = LANGUAGE_NAMES[code];
  if (language === "") return name ? `${name} (source)` : "Source language";
  return name ?? code.toUpperCase();
}

/** Compact badge for the languages a persona has cached: the first one, plus "+N" when
 *  there are more ("EN +1"). Every place that shows a persona's languages uses this, so
 *  the sidebar tree and the strip chip cannot drift apart again. */
export function formatLanguages(languages: string[], sourceLanguage?: string): string {
  const first = langCode(languages[0] ?? "", sourceLanguage);
  return languages.length > 1 ? `${first} +${languages.length - 1}` : first;
}

/** Every cached language spelled out, for the badge's tooltip: "English (source), Russian". */
export function languageListTitle(languages: string[], sourceLanguage?: string): string {
  if (languages.length === 0) return "Output language";
  return languages.map((language) => langName(language, sourceLanguage)).join(", ");
}
